export const FAQ_GUIDE = `
# Frequently Asked Questions

Find quick answers to common questions about VoiceThera.

---

## Account & Getting Started

### How do I reset my password?

1. Go to the login page
2. Click "Forgot Password"
3. Enter your email address
4. Check your inbox for the reset link
5. Click the link and create a new password

**Note:** Reset links expire after 24 hours.

---

### Can I change my email address?

Currently, direct email changes aren't supported. To use a different email:

1. Create a new account with your preferred email
2. Contact support to help migrate your session history and credits
3. Your biography and preferences will need to be re-entered

---

### How do I delete my account?

1. Go to **Settings** → **Account**
2. Scroll to the bottom
3. Click "Delete Account"
4. Confirm the deletion

**Warning:** This permanently deletes all your sessions, biographies, and credits. This action cannot be undone.

---

### Can I have multiple accounts?

Yes, you can create multiple accounts with different email addresses. However, credits and subscriptions cannot be shared between accounts.

---

### What browsers and devices are supported?

VoiceThera works best on:

- **Desktop:** Chrome, Firefox, Safari, Edge (latest versions)
- **Mobile:** Safari (iOS), Chrome (Android)
- **Tablets:** Full support on iPad and Android tablets

For best audio quality, use headphones and ensure a stable internet connection.

---

## Sessions & Audio

### Why won't my audio play?

Common solutions:

1. **Check your browser permissions** - Allow audio autoplay
2. **Update your browser** - Use the latest version
3. **Try incognito mode** - Rules out extension conflicts
4. **Check your internet** - Slow connections may cause buffering
5. **Clear browser cache** - Sometimes helps with playback issues

If the problem persists, try downloading the session and playing it locally.

---

### Can I download sessions for offline use?

Yes! Every session has a download button. Click the download icon next to the session to save it as an MP3 file to your device. You can then play it in any audio player, even without internet.

---

### How long does it take to generate a session?

Typical generation times:

- **3-minute session:** 30-60 seconds
- **5-minute session:** 60-90 seconds
- **7-minute session:** 90-120 seconds
- **10-minute session:** 2-3 minutes

Generation time depends on server load and session complexity. If generation takes longer than 5 minutes, refresh the page and try again.

---

### Why does my session sound generic?

To improve personalization:

1. **Complete your biography** - The more detail, the better
2. **Answer all questionnaire fields** - Don't skip optional questions
3. **Use specific details** - Instead of "stressed," say "stressed about upcoming presentation"
4. **Update your biography regularly** - Keep it current with your life

Sessions are only as personalized as the information you provide.

---

### Can I regenerate a session if I don't like it?

Yes! You can create a new session with the same category and settings. Each generation uses your latest biography and questionnaire answers, so results will vary. However, regenerating consumes additional credits.

---

### What voices are available?

VoiceThera currently uses professional AI voices optimized for meditation and wellness content. Voice options are selected automatically based on session type and category to provide the most therapeutic experience.

---

### Can I use my own voice?

Custom voice upload is not currently supported. VoiceThera uses professionally trained AI voices specifically designed for therapeutic content delivery.

---

## Credits & Billing

### What payment methods do you accept?

We accept:

- Credit cards (Visa, Mastercard, American Express, Discover)
- Debit cards
- Digital wallets (Apple Pay, Google Pay)

All payments are processed securely through Stripe.

---

### How much do sessions cost?

Credit costs per session length:

- **3 minutes:** 10 credits
- **5 minutes:** 17 credits
- **7 minutes:** 23 credits
- **10 minutes:** 33 credits

**Credit Packs:**
- **Starter:** $10 for 100 credits (~10 sessions)
- **Value:** $27 for 330 credits (~33 sessions)
- **Popular:** $49 for 690 credits (~69 sessions)
- **Pro:** $84 for 1,440 credits (~144 sessions)
- **Ultimate:** $150 for 3,125 credits (~312 sessions)

---

### Can I get a refund?

Refund eligibility:

- **Subscription:** Cancel anytime; no refunds for partial months
- **Credit packs:** Refundable within 7 days if unused
- **Used credits:** Non-refundable once sessions are generated

Contact support within 7 days of purchase for refund requests.

---

### Why was I charged twice?

Double charges usually occur due to:

1. **Browser refresh during checkout** - Creates duplicate orders
2. **Multiple tab submissions** - Same order submitted twice
3. **Bank authorization holds** - Temporary holds that disappear in 3-5 days

Check your email for order confirmations. If you see two confirmation emails, contact support immediately for a refund.

---

### How do I cancel my subscription?

1. Go to **Settings** → **Plans & Credits**
2. Find your active subscription
3. Click "Cancel Subscription"
4. Confirm cancellation

You'll retain access until the end of your billing period. Monthly credits remain available until expiration.

---

### Do credits expire?

- **Purchased credits:** Never expire
- **Monthly subscription credits:** Expire at the end of each billing cycle (don't roll over)
- **Free signup credits:** Never expire
- **Bonus/promotional credits:** May have expiration dates (check email notification)

---

### Can I transfer credits to another account?

No, credits are non-transferable between accounts. However, you can use Audio Gifts to share sessions with others.

---

## Biography & Personalization

### How do I improve session personalization?

**Write a detailed biography (1500-3000 characters recommended):**

- **Background:** Age, occupation, life stage
- **Current challenges:** Work stress, sleep issues, relationship dynamics
- **Personal context:** Family, hobbies, values, spirituality
- **Wellness goals:** What you're working toward
- **Specific details:** Names, places, meaningful experiences

**Example:** Instead of "I'm stressed," write "I'm a 34-year-old software engineer juggling a demanding startup job while caring for two young kids. I barely sleep, skip meals, and feel guilty when I take time for myself."

---

### Can I have multiple biographies?

Yes! The **Biography Library** allows you to create up to 10 biographies:

- 1 default biography (your own)
- Up to 9 additional biographies (for loved ones)

Use these for creating Audio Gifts or switching contexts (e.g., work vs. personal life).

---

### What's the Biography Library?

The Biography Library stores biographical profiles for you and loved ones. This eliminates re-entering information when creating gifts or personalized sessions. You can:

- Create up to 10 biographies
- Set one as your default
- Edit/delete biographies anytime
- Use them for gifts or personal sessions

Access it via **Biography Library** in the sidebar.

---

### Is my biography data secure?

Yes. Your biography is:

- Encrypted in transit and at rest
- Never shared with third parties
- Used only to personalize your sessions
- Not used for advertising or data mining
- Protected by industry-standard security

You can delete your biography and all associated data at any time.

---

### How do I edit my biography?

1. Go to **Biography Library** or **Profile Settings**
2. Click "Edit" on the biography you want to change
3. Update the text (3000 character limit)
4. Click "Save Changes"

Changes apply immediately to future sessions.

---

## Audio Gifts

### What is an Audio Gift?

An Audio Gift is a personalized meditation session you create for a loved one. It blends:

- Your name as the sender
- Their biography from your Biography Library
- A personal message
- Category-specific questionnaire answers

Recipients receive a beautiful landing page with the audio gift, playable before they even sign up.

---

### How do I send a gift to someone?

1. Click **Send Audio Gift** in the sidebar
2. **Select recipient** from your Biography Library (or create new)
3. **Choose category** and session length
4. **Answer questionnaire** about their needs
5. **Add personal message** and confirm your name
6. **Generate and share** via link, QR code, or social media

---

### Does the recipient need an account?

No! Recipients can listen to the gift immediately without signing up. However, if they create an account, they can:

- Save the gift to their library
- Create their own sessions
- Send gifts to others

Plus, you earn 5 bonus credits when they sign up!

---

### How many credits does a gift cost?

Audio Gifts use the same credit costs as personal sessions:

- **3 minutes:** 10 credits
- **5 minutes:** 17 credits
- **7 minutes:** 23 credits
- **10 minutes:** 33 credits

**Free tier limits:** 2 gifts per month
**Basic subscribers:** 10 gifts per month
**Pro subscribers:** Unlimited gifts

---

### Can I preview the gift before sending?

Yes! After generation, you can:

- Play the full audio
- Download it
- Create a mixed version with background music
- Preview the gift landing page

Only proceed to sharing when you're satisfied with the result.

---

## Technical Issues & Troubleshooting

### The app is running slow - what can I do?

Try these steps:

1. **Clear browser cache** - Settings → Privacy → Clear browsing data
2. **Close unused tabs** - Reduces memory usage
3. **Update your browser** - Use the latest version
4. **Check internet speed** - Run a speed test (need 5+ Mbps)
5. **Restart your device** - Clears memory leaks
6. **Try incognito mode** - Rules out extension conflicts

---

### Generation failed - what happened?

Common causes:

- **Server overload** - Try again in 5 minutes
- **Biography too long** - Ensure under 3000 characters
- **Invalid questionnaire input** - Check for special characters
- **Network timeout** - Check your internet connection
- **Insufficient credits** - Verify your credit balance

If the error persists, contact support with the error message.

---

### Audio cuts out during playback

Solutions:

1. **Improve internet connection** - Switch to Wi-Fi or stronger signal
2. **Download the session** - Play offline
3. **Lower browser tabs** - Reduce memory usage
4. **Update browser** - Fix known audio bugs
5. **Disable extensions** - Try incognito mode

---

### Page won't load or shows error

Troubleshooting steps:

1. **Hard refresh** - Press Ctrl+Shift+R (PC) or Cmd+Shift+R (Mac)
2. **Clear cache** - Browser settings → Clear data
3. **Check status** - Visit status.voicethera.com (if available)
4. **Try different browser** - Rules out browser-specific issues
5. **Check for updates** - Update your browser

---

### How do I report a bug?

1. Go to **Help** in the sidebar
2. Click "Contact Support"
3. Describe the issue in detail:
   - What you were doing
   - What went wrong
   - Error messages (screenshot if possible)
   - Browser and device info
4. Submit the form

We respond within 24 hours.

---

## Privacy & Security

### Is my data secure?

Yes. VoiceThera uses:

- **Encryption:** All data encrypted in transit (HTTPS) and at rest
- **Secure authentication:** Industry-standard auth protocols
- **Regular security audits:** Third-party penetration testing
- **No data selling:** We never sell or share your personal data
- **GDPR compliant:** Full data protection compliance

---

### Who can see my sessions?

Your sessions are **completely private**:

- Only you can access your sessions
- VoiceThera staff cannot view your sessions
- Sessions are not used for training or analytics
- Audio Gifts are only accessible via unique private links

Exception: Organization admin sessions may be visible to organization admins for analytics.

---

### Do you sell my data?

**Absolutely not.** We do not:

- Sell your data to third parties
- Use your data for advertising
- Share your biography with anyone
- Train AI models on your content

Your data exists solely to personalize your sessions.

---

### How do I delete my data?

1. Go to **Settings** → **Account**
2. Click "Delete Account"
3. Confirm deletion

This permanently deletes:

- All sessions and audio files
- Your biography and preferences
- Credit balance (non-refundable)
- Account login credentials

Deletion is permanent and cannot be undone.

---

### Are sessions recorded or monitored?

No. Your sessions are:

- Generated on-demand
- Not monitored by staff
- Not used for quality assurance without consent
- Not analyzed for content themes

We only track anonymous usage metrics (e.g., "session generated") for platform improvement.

---

## Mobile & Apps

### Is there a mobile app?

VoiceThera is currently a **web-based application** accessible through your mobile browser. We're exploring native iOS and Android apps based on user demand.

---

### How do I use VoiceThera on my phone?

1. Open your mobile browser (Safari on iOS, Chrome on Android)
2. Go to voicethera.com
3. Log in to your account
4. Use the full app - all features work on mobile!

**Tip:** Add VoiceThera to your home screen:
- **iOS:** Tap Share → Add to Home Screen
- **Android:** Tap Menu → Add to Home Screen

---

### Can I use it offline?

Yes, with downloads:

1. Generate your session while online
2. Click the download button
3. Save the MP3 to your device
4. Play offline in any audio app

The web app requires internet for generation, but downloaded sessions work offline.

---

### Does it work on tablets?

Yes! VoiceThera is fully optimized for:

- iPad (all models)
- Android tablets
- Surface tablets

The responsive design adapts to tablet screens for a great experience.

---

## Still Have Questions?

If you can't find the answer you're looking for:

1. **Search this Help Center** - Use the search bar at the top
2. **Check other guides** - Browse specialized guides for detailed topics
3. **Contact Support** - Visit the Help page and submit a support request
4. **Community Forum** - Connect with other users (coming soon)

We're here to help!
`;
